import { z } from 'zod'
import { frameSequenceSchema } from '../frame-sequence'

export const SETUP_CHAT_CONTEXT_MESSAGE_TYPE = 'onestep:setup-chat-context'
export const SETUP_CHAT_OPENED_MESSAGE_TYPE = 'onestep:setup-chat-opened'
export const SETUP_CHAT_NEW_REQUESTED_MESSAGE_TYPE = 'onestep:setup-chat-new-requested'
export const SETUP_CHAT_REOPEN_REQUESTED_MESSAGE_TYPE = 'onestep:setup-chat-reopen-requested'
export const SETUP_CHAT_REOPEN_ACCEPTED_MESSAGE_TYPE = 'onestep:setup-chat-reopen-accepted'
export const SETUP_CHAT_HISTORY_MESSAGE_TYPE = 'onestep:setup-chat-history'
export const SETUP_APPLIED_MESSAGE_TYPE = 'onestep:setup-applied'
export const SETUP_PREVIEW_TARGET_MESSAGE_TYPE = 'onestep:setup-preview-target'
export const SETUP_PREVIEW_SHOWN_MESSAGE_TYPE = 'onestep:setup-preview-shown'

export const SETUP_CHAT_CHROME_PARAM = 'chrome'
export const SETUP_CHAT_FRAME_CHROME = 'frame'

const scopeFields = { workspace: z.string().optional() }
const dependentBreakRefusalClassSchema = z.enum([
  'cohort-dependent-break',
  'portal-dependent-break',
])
export type DependentBreakRefusalClass = z.infer<typeof dependentBreakRefusalClassSchema>
const dependentBreakTargetSurfaceSchema = z.enum(['cohort', 'view'])
export type DependentBreakTargetSurface = z.infer<typeof dependentBreakTargetSurfaceSchema>
const dependentBreakChangeSchema = z.enum([
  'delete',
  'unpublish',
  'rename',
  'edit',
  'move',
  'hide',
])
export type DependentBreakChange = z.infer<typeof dependentBreakChangeSchema>
export const setupEntrySchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('main'), ...scopeFields }),
  z.strictObject({ kind: z.literal('cohorts'), ...scopeFields }),
  z.strictObject({ kind: z.literal('category'), ...scopeFields, category: z.string() }),
  z.strictObject({ kind: z.literal('view'), ...scopeFields, view: z.string() }),
  z.strictObject({
    kind: z.literal('columns'),
    ...scopeFields,
    view: z.string().optional(),
    column: z.string().min(1).optional(),
  }),
  z.strictObject({
    kind: z.literal('dependent_break'),
    ...scopeFields,
    refusal_class: dependentBreakRefusalClassSchema,
    target: z.strictObject({
      surface: dependentBreakTargetSurfaceSchema,
      key: z.string().min(1),
    }),
    change: dependentBreakChangeSchema,
  }),
  z.strictObject({ kind: z.literal('recipe'), recipe: z.string() }),
  z.strictObject({
    kind: z.literal('draft'),
    working_copy: z
      .string()
      .min(1)
      .transform(workingCopy => workingCopy.toUpperCase()),
  }),
])
export type SetupEntry = z.infer<typeof setupEntrySchema>

const setupEntryKinds: ReadonlySet<string> = new Set(
  setupEntrySchema.options.map(option => option.shape.kind.value),
)
export const resumedSetupEntrySchema = z.union([
  setupEntrySchema,
  z
    .looseObject({ kind: z.string().refine(kind => !setupEntryKinds.has(kind)) })
    .transform(() => undefined),
])

export const setupRosterPreviewTargetSchema = z.object({
  workingCopyId: z.string().min(1),
  proposalRevision: z.number().int().positive(),
})
export type SetupRosterPreviewTarget = z.infer<typeof setupRosterPreviewTargetSchema>

export const setupChatFrameMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal(SETUP_CHAT_OPENED_MESSAGE_TYPE), sessionId: z.string().min(1) }),
  z.object({ type: z.literal(SETUP_CHAT_NEW_REQUESTED_MESSAGE_TYPE) }),
  z.object({
    type: z.literal(SETUP_CHAT_REOPEN_REQUESTED_MESSAGE_TYPE),
    requestId: z.string(),
    sessionId: z.string().min(1),
    workspace: z.string().min(1),
  }),
  z.object({
    type: z.literal(SETUP_APPLIED_MESSAGE_TYPE),
    sessionId: z.string().min(1),
    proposalId: z.string().min(1),
    revision: z.number().int().positive(),
  }),
  z.object({
    type: z.literal(SETUP_PREVIEW_TARGET_MESSAGE_TYPE),
    sessionId: z.string().min(1),
    target: setupRosterPreviewTargetSchema.nullable(),
  }),
])
export type SetupChatFrameMessage = z.infer<typeof setupChatFrameMessageSchema>

export const setupChatStartSchema = z.enum(['create-view'])
export type SetupChatStart = z.infer<typeof setupChatStartSchema>

export const setupChatContextSchema = z.object({
  type: z.literal(SETUP_CHAT_CONTEXT_MESSAGE_TYPE),
  entry: setupEntrySchema,
  workspaceNames: z.record(z.string(), z.string()),
  viewName: z.string().optional(),
  categoryName: z.string().optional(),
  cohortName: z.string().optional(),
  columnName: z.string().optional(),
  targetName: z.string().optional(),
  rosterPreview: z.boolean().optional(),
  start: setupChatStartSchema.optional().catch(undefined),
})
export type SetupChatContext = z.infer<typeof setupChatContextSchema>

export const setupChatHistorySchema = z.object({
  type: z.literal(SETUP_CHAT_HISTORY_MESSAGE_TYPE),
  sequence: frameSequenceSchema,
})
export type SetupChatHistory = z.infer<typeof setupChatHistorySchema>

export const setupChatReopenAcceptedSchema = z.object({
  type: z.literal(SETUP_CHAT_REOPEN_ACCEPTED_MESSAGE_TYPE),
  requestId: z.string(),
})
export type SetupChatReopenAccepted = z.infer<typeof setupChatReopenAcceptedSchema>

export const setupPreviewShownSchema = z.object({
  type: z.literal(SETUP_PREVIEW_SHOWN_MESSAGE_TYPE),
  sessionId: z.string().min(1),
  ...setupRosterPreviewTargetSchema.shape,
})
export type SetupPreviewShown = z.infer<typeof setupPreviewShownSchema>

export const setupChatHostMessageSchema = z.discriminatedUnion('type', [
  setupChatContextSchema,
  setupChatHistorySchema,
  setupChatReopenAcceptedSchema,
  setupPreviewShownSchema,
])
export type SetupChatHostMessage = z.infer<typeof setupChatHostMessageSchema>

export const parseSetupChatHostMessage = (data: unknown): SetupChatHostMessage | undefined => {
  const parsed = setupChatHostMessageSchema.safeParse(data)
  return parsed.success ? parsed.data : undefined
}
